import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Wallet, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicExpense() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ lr_number: "", amount: "", reason: "", category: "Misc", bank_account_id: "", submitted_by: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/p/expense/${token}/meta`)
      .then((r) => setMeta(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "This expense link is not valid."));
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/p/expense/${token}`, {
        ...form, amount: Number(form.amount),
      });
      setDone(data);
      setForm({ lr_number: "", amount: "", reason: "", category: "Misc", bank_account_id: "", submitted_by: form.submitted_by });
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not submit expense.");
    } finally { setBusy(false); }
  }

  if (err && !meta) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#8A2526] text-sm p-6" data-testid="public-expense-error">{err}</div>;
  if (!meta) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#68665E] text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="public-expense-page">
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/ito-logo.png" alt="ITO" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-headings font-bold text-[#1A1A18] text-sm leading-tight">{meta.company_name || "INDIAN TRANSPORT ORGANISATION"}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#68665E]">Submit Expense</div>
          </div>
        </div>

        {done ? (
          <div className="bg-white border border-[#E5E3DB] p-6 text-center" data-testid="public-expense-success">
            <CheckCircle2 className="w-10 h-10 text-[#2F6F4E] mx-auto mb-3" />
            <div className="font-medium text-[#1A1A18]">{done.lr_number ? `Expense recorded on ${done.lr_number}` : "Expense recorded"}</div>
            <div className="text-sm text-[#68665E] mt-1">Rs. {Number(done.amount).toLocaleString("en-IN")}</div>
            <button className="btn-primary mt-5" onClick={() => setDone(null)} data-testid="public-expense-another">Add another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#E5E3DB] p-6 space-y-4">
            <div className="flex items-center gap-2 text-[#C84B31]"><Wallet className="w-5 h-5" /><span className="font-medium text-[#1A1A18]">New Expense</span></div>
            <div>
              <label className="input-label">LR Number <span className="text-[10px] text-[#A09E96]">(optional — leave blank for non-LR expenses like staff travel)</span></label>
              <select className="input-field font-mono" value={form.lr_number} onChange={(e) => setForm({ ...form, lr_number: e.target.value })} data-testid="public-expense-lr">
                <option value="">— Not for a specific LR —</option>
                {(meta.lrs || []).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="input-label">Amount (Rs.) *</label>
                <input type="number" step="0.01" className="input-field" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="public-expense-amount" />
              </div>
              <div>
                <label className="input-label">Category</label>
                <select className="input-field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="public-expense-category">
                  {meta.categories.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="input-label">Reason</label>
              <input className="input-field" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Diesel at Talegaon pump" data-testid="public-expense-reason" />
            </div>
            {meta.bank_accounts.length > 0 && (
              <div>
                <label className="input-label">Paid from account</label>
                <select className="input-field" value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} data-testid="public-expense-bank">
                  <option value="">— Not specified —</option>
                  {meta.bank_accounts.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="input-label">Your name (staff)</label>
              <select className="input-field" value={form.submitted_by} onChange={(e) => setForm({ ...form, submitted_by: e.target.value })} data-testid="public-expense-by">
                <option value="">— Select staff (optional) —</option>
                {(meta.staff || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {err && <div className="text-sm text-[#8A2526]" data-testid="public-expense-submit-error">{err}</div>}
            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="public-expense-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? "Submitting…" : "Submit Expense"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
